import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import Button from "../../theme/components/Button.jsx";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { backendGet } from "../../services/dataService.js";
import { useUser } from "../../hooks/user.js";
import { useSnackbar } from "notistack";
import { useSubstitutionActions } from "../../hooks/useSubstitutionActions.jsx";
import Form from "../Form/Form.jsx";
import FormAutocomplete from "../Form/FormAutocomplete.jsx";
import FormDateTimePicker from "../Form/FormDateTimePicker.jsx";
import FormRadioGroup from "../Form/FormRadioGroup.jsx";
import SubmitButton from "../Form/SubmitButton.jsx";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// convert local time to UTC for backend
dayjs.extend(utc);

const fetchUsers = async (owner_id) => {
  let users = [];
  await backendGet(`data/user/possible-substitutes/${owner_id}`, {
    setResult: (result) => {
      users = result.data;
    },
  });
  return users;
};

const shouldDisableTime = (value, view) => {
  const dayjsValue = dayjs(value);
  const allowedHours = [0, 8, 12, 16, 23];

  if (view === "hours") {
    // Disable all hours not in the allowed list
    return !allowedHours.includes(dayjsValue.hour());
  }

  if (view === "minutes") {
    const hour = dayjsValue.hour();
    // For hour 23, only allow minute 59
    if (hour === 23) {
      return dayjsValue.minute() !== 59;
    }
    // For all other allowed hours, only allow minute 0
    if (allowedHours.includes(hour)) {
      return dayjsValue.minute() !== 0;
    }
    // Disable all minutes for any other hour (shouldn't be reachable)
    return true;
  }

  return false;
};

const userSelectionSchema = yup.object({
  user_id: yup
    .mixed()
    .required("Benutzer ist erforderlich")
    .test(
      "is-valid-user",
      "Benutzer ist erforderlich",
      (value) => value && typeof value === "object" && value.id
    ),
});

const substitutionSchema = yup.object({
  substitute_user_id: yup
    .number("Bitte wählen Sie eine Stellvertretung aus.")
    .required("Bitte wählen Sie eine Stellvertretung aus."),
  notification_mode: yup.string().oneOf(["redirect", "copy"]).required(),
  starts_at: yup
    .date()
    .nullable()
    .typeError("Bitte geben Sie ein gültiges Startdatum ein.")
    .test(
      "valid-date",
      "Bitte geben Sie ein gültiges Startdatum ein.",
      (value) => {
        if (!value) return true; // Allow null/empty
        return dayjs(value).isValid();
      }
    ),
  ends_at: yup
    .date()
    .nullable()
    .typeError("Bitte geben Sie ein gültiges Enddatum ein.")
    .test(
      "valid-date",
      "Bitte geben Sie ein gültiges Enddatum ein.",
      (value) => {
        if (!value) return true; // Allow null/empty
        return dayjs(value).isValid();
      }
    )
    .when("starts_at", (starts_at, schema) => {
      if (starts_at && dayjs.isDayjs(starts_at) && starts_at.isValid()) {
        return schema.min(
          starts_at.toDate(),
          "Das Enddatum muss nach dem Beginndatum liegen."
        );
      }
      return schema;
    }),
});

const SubstitutionModal = ({
  open,
  onClose,
  onSuccess = () => {},
  substitution = null,
  isEdit = false,
}) => {
  const { user } = useUser();
  const canSelectSubstitutee =
    user?.role_names?.includes("sysadmin") ||
    user?.role_names?.includes("kd_verwaltung");

  const [step, setStep] = useState(canSelectSubstitutee ? 1 : 2);
  const [selectedUser, setSelectedUser] = useState(
    canSelectSubstitutee ? null : user
  );
  const { createSubstitution, updateSubstitution } = useSubstitutionActions();
  const { enqueueSnackbar } = useSnackbar();

  // Reset step when modal closes
  useEffect(() => {
    if (!open) {
      setStep(canSelectSubstitutee ? 1 : 2);
      setSelectedUser(canSelectSubstitutee ? null : user);
    }
  }, [open, canSelectSubstitutee, user]);

  // Skip to step 2 for edit mode or when user can only substitute themselves
  useEffect(() => {
    if (open) {
      if (isEdit && substitution) {
        setStep(2);
        setSelectedUser(substitution.user);
      } else if (!isEdit) {
        if (canSelectSubstitutee) {
          setStep(1);
        } else {
          setSelectedUser(user);
          setStep(2);
        }
      }
    }
  }, [isEdit, substitution, open, canSelectSubstitutee, user]);

  const handleStep1Submit = (data) => {
    setSelectedUser(data.user_id);
    setStep(2);
  };

  const handleStep2Submit = async (data, formMethods) => {
    try {
      const submission_data = {
        substitute_user_id: data.substitute_user_id,
        starts_at: data.starts_at
          ? dayjs(data.starts_at).utc().format("YYYY-MM-DD HH:mm:ss")
          : null,
        ends_at: data.ends_at
          ? dayjs(data.ends_at).utc().format("YYYY-MM-DD HH:mm:ss")
          : null,
        notification_mode: data.notification_mode,
      };

      if (isEdit && substitution) {
        await updateSubstitution(
          substitution.user.id,
          substitution.id,
          submission_data
        );
      } else {
        await createSubstitution(selectedUser.id, submission_data);
      }

      onSuccess();
      onClose();
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const validation_errors = error.response.data.errors;
        Object.entries(validation_errors).forEach(([field, messages]) => {
          formMethods.setError(field, {
            type: "server",
            message: messages[0],
          });
        });
      } else {
        enqueueSnackbar("Ein unerwarteter Fehler ist aufgetreten.", {
          variant: "error",
        });
        console.error("Submission failed:", error);
      }
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const fetchSubstitutableUsers = async () => {
    let users = [];
    await backendGet("data/user/substitutable-users", {
      setResult: (result) => {
        users = result.data;
      },
    });
    return users;
  };

  const selectedUserName = selectedUser?.full_name
    ? selectedUser?.full_name
    : `${selectedUser?.title || ""} ${selectedUser?.vorname || ""} ${selectedUser?.nachname || ""}`.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit
          ? "Stellvertretung bearbeiten"
          : step === 1
            ? "Benutzer auswählen"
            : `Stellvertretung für ${selectedUserName || ""} erstellen`}
      </DialogTitle>
      {step === 1 ? (
        // Step 1: User Selection
        <Form
          onSubmit={handleStep1Submit}
          defaultValues={{ user_id: null }}
          resolver={yupResolver(userSelectionSchema)}
        >
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
            >
              <FormAutocomplete
                name="user_id"
                label="Benutzer (wird vertreten)"
                fetchOptions={fetchSubstitutableUsers}
                getOptionLabel={(option) => `${option.full_name}`}
                transformValue={(option) => option}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ paddingX: 3, paddingBottom: 2 }}>
            <Button variant="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <SubmitButton>Weiter</SubmitButton>
          </DialogActions>
        </Form>
      ) : (
        // Step 2: Substitution Form
        <Form
          key={isEdit && substitution ? substitution.id : "new"}
          onSubmit={handleStep2Submit}
          defaultValues={{
            substitute_user_id: substitution?.substitute?.id || null,
            notification_mode: substitution?.notification_mode || "redirect",
            starts_at: substitution?.starts_at
              ? dayjs(substitution.starts_at)
              : null,
            ends_at: substitution?.ends_at ? dayjs(substitution.ends_at) : null,
          }}
          resolver={yupResolver(substitutionSchema)}
        >
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
            >
              {!isEdit && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Benutzer: {selectedUserName}
                </Typography>
              )}

              <FormAutocomplete
                name="substitute_user_id"
                label="Stellvertreter"
                fetchOptions={() => fetchUsers(selectedUser?.id)}
                getOptionLabel={(option) => `${option.full_name}`}
              />

              <FormDateTimePicker
                name="starts_at"
                label="Beginn (optional)"
                ampm={false}
                shouldDisableTime={shouldDisableTime}
                timeSteps={{ minutes: 1 }}
                skipDisabled={true}
              />

              <FormDateTimePicker
                name="ends_at"
                label="Ende (optional)"
                ampm={false}
                shouldDisableTime={shouldDisableTime}
                timeSteps={{ minutes: 1 }}
                minutesStep={1}
                skipDisabled={true}
              />

              <FormRadioGroup
                name="notification_mode"
                label="Benachrichtigungen & Mails"
                options={[
                  { value: "redirect", label: "Umleiten" },
                  { value: "copy", label: "Kopieren" },
                ]}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ paddingX: 3, paddingBottom: 2 }}>
            {step === 2 && !isEdit && canSelectSubstitutee && (
              <Button variant="tertiary" onClick={handleBack}>
                Zurück
              </Button>
            )}
            <Button variant="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <SubmitButton>{isEdit ? "Speichern" : "Erstellen"}</SubmitButton>
          </DialogActions>
        </Form>
      )}
    </Dialog>
  );
};

SubstitutionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  substitution: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default SubstitutionModal;
